import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageShell from "@/components/PageShell";
import CarpoolTripMap from "@/components/CarpoolTripMap";
import {
  ArrowLeft, Car, MapPin, Clock, Users, Send, ShieldCheck, Star,
  CheckCircle2, X, UserCheck, UserX, Hourglass, MessageSquare,
  Calendar, Phone, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

function formatDateFR(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function CovoiturageTripPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("info"); // info | chat | rate

  const load = () => {
    setLoading(true);
    api.get(`/carpool/${id}`)
      .then((r) => setTrip(r.data))
      .catch(() => { toast.error("Trajet introuvable"); navigate("/covoiturage"); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) {
    return (
      <PageShell>
        <div className="max-w-3xl mx-auto px-5 py-10">
          <div className="phenix-card p-10 animate-pulse h-64" />
        </div>
      </PageShell>
    );
  }
  if (!trip) return null;

  const uid = user ? String(user.id) : null;
  const isDriver = uid && trip.driver_id === uid;
  const myApp = (trip.applications || []).find((a) => a.user_id === uid);
  const isAcceptedPassenger = !!myApp && myApp.status === "accepted";
  const canChat = isDriver || isAcceptedPassenger;
  const remaining = Math.max(0, (trip.seats || 0) - (trip.seats_taken || 0));
  const driverProfile = trip.driver_profile;

  return (
    <PageShell>
      <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/covoiturage" className="inline-flex items-center gap-1.5 text-[13px] text-plum-700/65 hover:text-plum-900 mb-4" data-testid="trip-back">
          <ArrowLeft size={14} /> Retour aux trajets
        </Link>

        {/* Header */}
        <div className="phenix-card bg-white p-5 sm:p-7 mb-5">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div className="flex-1 min-w-0">
              <div className="phenix-eyebrow mb-2 inline-flex items-center gap-1.5">
                <Car size={11} strokeWidth={2.5} /> Trajet co-voiturage
              </div>
              <h1 className="heading-display text-[1.6rem] sm:text-[2rem] text-plum-900 leading-tight">
                {trip.from_location} <span className="text-plum-500">→</span> {trip.to_location}
              </h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2 text-[13px] text-plum-700/75">
                {trip.trip_date && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar size={13} className="text-plum-500" />
                    {formatDateFR(trip.trip_date)}
                  </span>
                )}
                {(trip.trip_time || trip.time) && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={13} className="text-plum-500" />
                    {trip.trip_time || trip.time}
                    {trip.recurring && " · hebdomadaire"}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <Users size={13} className="text-plum-500" />
                  {remaining}/{trip.seats} place{trip.seats > 1 ? "s" : ""} libre{remaining !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
            <span
              className={`phenix-pill ${remaining > 0 ? "bg-mint-100 text-mint-700" : "bg-cream-100 text-plum-700/55"}`}
            >
              {remaining > 0 ? `${remaining} place${remaining > 1 ? "s" : ""}` : "Complet"}
            </span>
          </div>

          {/* Carte */}
          <CarpoolTripMap
            fromLat={trip.from_lat}
            fromLng={trip.from_lng}
            toLat={trip.to_lat}
            toLng={trip.to_lng}
            fromLabel={trip.from_location}
            toLabel={trip.to_location}
            height={260}
          />
          <CarpoolTripMap.Legend />

          {trip.note && (
            <p className="mt-4 text-[13px] text-plum-700/75 italic border-l-2 border-mint-400 pl-3">
              « {trip.note} »
            </p>
          )}
        </div>

        {/* Profil chauffeur */}
        {driverProfile && (
          <div className="phenix-card bg-white p-5 mb-5" data-testid="driver-profile">
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 rounded-2xl bg-plum-100 text-plum-700 grid place-items-center font-display font-extrabold text-xl shrink-0">
                {(driverProfile.pseudo || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-0.5">
                  <h3 className="font-display font-extrabold text-plum-900 text-base">{driverProfile.pseudo}</h3>
                  {driverProfile.residence_verified && (
                    <span className="phenix-pill bg-mint-100 text-mint-700 inline-flex items-center gap-1 text-[10px]">
                      <ShieldCheck size={10} /> Vérifié
                    </span>
                  )}
                  {driverProfile.role === "commercant" && (
                    <span className="phenix-pill bg-sun-100 text-sun-700 text-[10px]">Commerçant</span>
                  )}
                </div>
                <p className="text-[12px] text-plum-700/65">
                  {driverProfile.rating_count > 0 ? (
                    <span className="inline-flex items-center gap-1">
                      <Star size={11} className="text-sun-500 fill-sun-500" />
                      <strong className="text-plum-900">{driverProfile.rating_avg.toFixed(1)}</strong>
                      <span className="text-plum-700/55">/ 5</span>
                      <span className="text-plum-700/55">· {driverProfile.rating_count} avis</span>
                    </span>
                  ) : (
                    <span className="text-plum-700/55 italic">Aucun avis pour le moment</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto" data-testid="trip-tabs">
          <TabBtn active={tab === "info"} onClick={() => setTab("info")} icon={Car}>Candidatures</TabBtn>
          {canChat && <TabBtn active={tab === "chat"} onClick={() => setTab("chat")} icon={MessageSquare}>Discussion</TabBtn>}
          {isAcceptedPassenger && <TabBtn active={tab === "rate"} onClick={() => setTab("rate")} icon={Star}>Noter</TabBtn>}
        </div>

        {tab === "info" && (
          <TripInfoTab trip={trip} isDriver={isDriver} myApp={myApp} remaining={remaining} reload={load} user={user} />
        )}
        {tab === "chat" && canChat && <ChatTab tripId={trip.id} userId={uid} />}
        {tab === "rate" && isAcceptedPassenger && <RateTab tripId={trip.id} onDone={load} />}
      </div>
    </PageShell>
  );
}

function TabBtn({ active, onClick, icon: Icon, children }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold transition-all ${
        active ? "bg-plum-700 text-white shadow-md" : "bg-white text-plum-700 border border-cream-200 hover:bg-cream-50"
      }`}
    >
      <Icon size={13} /> {children}
    </button>
  );
}

function TripInfoTab({ trip, isDriver, myApp, remaining, reload, user }) {
  const apps = trip.applications || [];
  const [busy, setBusy] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Avantage solidaire dispo pour ce trajet ?
  const [solidaryPerk, setSolidaryPerk] = useState(null);
  const [useSolidary, setUseSolidary] = useState(false);

  useEffect(() => {
    if (!user || isDriver || myApp) return;
    api.get("/me/perks").then((r) => {
      const perks = r.data?.perks || [];
      const eligible = perks.find((p) => p.kind === "carpool_free" && p.remaining_this_month > 0);
      setSolidaryPerk(eligible || null);
    }).catch(() => {});
  }, [user, isDriver, myApp]);

  const moderate = async (appId, status) => {
    setBusy(appId);
    try {
      const r = await api.patch(`/carpool/${trip.id}/applications/${appId}`, { status });
      if (status === "accepted" && r.data?.driver_reward) {
        toast.success(`🎁 Vous avez gagné : ${r.data.driver_reward.title} — code ${r.data.driver_reward.code}`, { duration: 6000 });
      } else {
        toast.success(status === "accepted" ? "Candidature acceptée" : "Candidature refusée");
      }
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  };

  const submitApply = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = { message, phone };
      if (useSolidary && solidaryPerk) body.solidary_perk_id = solidaryPerk.id;
      await api.post(`/carpool/${trip.id}/apply`, body);
      toast.success(useSolidary ? "Candidature solidaire envoyée 🎁" : "Candidature envoyée !");
      setApplyOpen(false);
      setMessage(""); setPhone(""); setUseSolidary(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (isDriver) {
    return (
      <div className="phenix-card bg-white p-5" data-testid="driver-applications">
        <h3 className="font-display font-extrabold text-plum-900 text-base mb-3 inline-flex items-center gap-2">
          <UserCheck size={16} /> Candidatures reçues ({apps.length})
        </h3>
        {apps.length === 0 ? (
          <div className="text-center py-6">
            <Users size={26} className="mx-auto text-plum-700/30 mb-2" />
            <p className="text-[13px] text-plum-700/60">Aucune candidature pour l&apos;instant.</p>
          </div>
        ) : (
          <ul className="space-y-2.5">
            {apps.map((a) => (
              <li key={a.id} className={`phenix-card p-3 ${a.is_solidary ? "bg-mint-50 border-mint-300" : "bg-cream-50"}`} data-testid={`app-row-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-mint-100 text-mint-700 grid place-items-center font-display font-extrabold text-sm shrink-0">
                    {(a.user_pseudo || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 mb-0.5">
                      <Link to="#" className="text-plum-900 font-bold text-sm hover:underline">{a.user_pseudo}</Link>
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${
                        a.status === "accepted" ? "text-mint-600" : a.status === "rejected" ? "text-pink-500" : "text-sun-700"
                      }`}>
                        {a.status === "accepted" ? "Acceptée" : a.status === "rejected" ? "Refusée" : "En attente"}
                      </span>
                    </div>
                    {a.is_solidary && (
                      <div className="mb-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-mint-200 text-mint-800 text-[10px] font-bold" data-testid={`solidary-${a.id}`}>
                        🎁 {a.solidary_perk_title || "Trajet solidaire"} — vous gagnerez une récompense mairie
                      </div>
                    )}
                    {a.user_phone && a.status === "accepted" && (
                      <a href={`tel:${a.user_phone}`} className="text-[12px] text-mint-700 inline-flex items-center gap-1 mb-1">
                        <Phone size={11} /> {a.user_phone}
                      </a>
                    )}
                    {a.message && <p className="text-[12px] text-plum-700/80 italic leading-snug">« {a.message} »</p>}
                    {a.status === "pending" && (
                      <div className="flex gap-2 mt-2.5">
                        <button
                          onClick={() => moderate(a.id, "accepted")}
                          disabled={busy === a.id}
                          className="flex-1 rounded-full bg-mint-600 hover:bg-mint-700 text-white px-3 py-2 text-[12px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                          data-testid={`accept-${a.id}`}
                        >
                          <UserCheck size={12} /> Accepter
                        </button>
                        <button
                          onClick={() => moderate(a.id, "rejected")}
                          disabled={busy === a.id}
                          className="flex-1 rounded-full bg-white border border-cream-200 hover:bg-pink-50 text-plum-700/75 hover:text-pink-500 px-3 py-2 text-[12px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                          data-testid={`reject-${a.id}`}
                        >
                          <UserX size={12} /> Refuser
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // Passager
  if (!user) {
    return (
      <div className="phenix-card bg-white p-6 text-center">
        <p className="text-[14px] text-plum-700/75 mb-3">Connectez-vous pour candidater à ce trajet.</p>
        <Link to="/login" className="rounded-full bg-plum-700 hover:bg-plum-800 text-white px-5 py-2.5 text-[13px] font-bold inline-flex items-center gap-1.5">
          Se connecter <ChevronRight size={13} />
        </Link>
      </div>
    );
  }

  if (myApp) {
    return (
      <div className="phenix-card bg-white p-6 text-center" data-testid="my-application">
        {myApp.status === "accepted" ? (
          <>
            <div className="w-14 h-14 rounded-full bg-mint-100 text-mint-600 grid place-items-center mx-auto mb-2">
              <CheckCircle2 size={28} />
            </div>
            <h3 className="font-display font-extrabold text-plum-900 text-lg mb-1">Vous êtes accepté ✓</h3>
            <p className="text-[13px] text-plum-700/75">Ouvrez l&apos;onglet Discussion pour échanger avec le chauffeur.</p>
          </>
        ) : myApp.status === "rejected" ? (
          <>
            <div className="w-14 h-14 rounded-full bg-pink-100 text-pink-500 grid place-items-center mx-auto mb-2">
              <X size={28} />
            </div>
            <h3 className="font-display font-extrabold text-plum-900 text-lg mb-1">Candidature refusée</h3>
            <p className="text-[13px] text-plum-700/75">Vous pouvez chercher un autre trajet.</p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full bg-sun-100 text-sun-600 grid place-items-center mx-auto mb-2">
              <Hourglass size={28} />
            </div>
            <h3 className="font-display font-extrabold text-plum-900 text-lg mb-1">En attente du chauffeur</h3>
            <p className="text-[13px] text-plum-700/75">Vous serez notifié dès que le chauffeur a répondu.</p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="phenix-card bg-white p-5">
      {!applyOpen ? (
        <div className="text-center py-3">
          <h3 className="font-display font-extrabold text-plum-900 text-base mb-1">Intéressé ? Candidatez !</h3>
          <p className="text-[13px] text-plum-700/65 max-w-md mx-auto mb-3">
            Le chauffeur vérifiera votre profil avant d&apos;accepter. Vous serez notifié.
          </p>
          {remaining > 0 ? (
            <Button
              onClick={() => setApplyOpen(true)}
              className="rounded-full bg-mint-600 hover:bg-mint-700 text-white h-11 px-5 font-bold inline-flex items-center gap-2"
              data-testid="trip-apply-btn"
            >
              <Send size={14} /> Candidater à ce trajet
            </Button>
          ) : (
            <div className="rounded-full bg-cream-100 text-plum-700/55 px-5 py-2.5 text-[13px] font-bold inline-flex items-center gap-2">
              Trajet complet
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={submitApply} className="space-y-3">
          <h3 className="font-display font-extrabold text-plum-900 text-base">Votre candidature</h3>
          {solidaryPerk && (
            <label
              className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${
                useSolidary ? "bg-mint-50 border-mint-300" : "bg-cream-50 border-cream-200 hover:border-mint-200"
              }`}
              data-testid="solidary-toggle"
            >
              <input
                type="checkbox"
                checked={useSolidary}
                onChange={(e) => setUseSolidary(e.target.checked)}
                className="mt-0.5 rounded text-mint-600"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-bold text-plum-900 inline-flex items-center gap-1.5">
                  🎁 Utiliser mon avantage solidaire
                </div>
                <p className="text-[11px] text-plum-700/65 mt-0.5 leading-snug">
                  « {solidaryPerk.title} » — il vous reste <strong>{solidaryPerk.remaining_this_month}/{solidaryPerk.monthly_quota}</strong> ce mois. Le chauffeur sera informé et recevra une récompense de la mairie s&apos;il vous accepte.
                </p>
              </div>
            </label>
          )}
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Téléphone (optionnel)</label>
            <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="06 …" data-testid="apply-phone" className="rounded-xl h-11 bg-cream-50" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Message au chauffeur</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Présentez-vous brièvement : pourquoi ce trajet vous intéresse, votre point de prise en charge…"
              data-testid="apply-message"
              className="rounded-xl bg-cream-50"
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-full bg-mint-600 hover:bg-mint-700 text-white h-11 font-bold"
              data-testid="apply-submit"
            >
              {submitting ? "Envoi…" : "Envoyer"}
            </Button>
            <Button type="button" variant="outline" onClick={() => setApplyOpen(false)} className="rounded-full h-11 px-5">
              Annuler
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function ChatTab({ tripId, userId }) {
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const load = () => {
    api.get(`/carpool/${tripId}/messages`)
      .then((r) => setMessages(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 8000); // polling léger
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [tripId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSending(true);
    try {
      await api.post(`/carpool/${tripId}/messages`, { content });
      setContent("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  return (
    <div className="phenix-card bg-white p-4" data-testid="chat-tab">
      <div className="h-[360px] overflow-y-auto bg-cream-50/60 rounded-xl p-3 mb-3 space-y-2">
        {loading && <p className="text-center text-plum-700/55 text-[12px] py-6">Chargement…</p>}
        {!loading && messages.length === 0 && (
          <div className="text-center py-8 text-plum-700/55">
            <MessageSquare size={24} className="mx-auto mb-2 opacity-50" />
            <p className="text-[13px]">Premier message ? Présentez-vous au chauffeur !</p>
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === userId;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
              <div
                className={`max-w-[80%] px-3 py-2 rounded-2xl text-[13px] ${
                  mine
                    ? "bg-plum-700 text-white rounded-br-md"
                    : "bg-white border border-cream-200 text-plum-900 rounded-bl-md"
                }`}
              >
                {!mine && <div className="font-bold text-[11px] mb-0.5 text-mint-700">{m.sender_pseudo}</div>}
                <p className="whitespace-pre-wrap break-words">{m.content}</p>
                <div className={`text-[10px] mt-0.5 ${mine ? "text-white/60" : "text-plum-700/45"}`}>
                  {new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="flex gap-2">
        <Input
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Écrire un message…"
          className="rounded-full bg-cream-50 h-11 flex-1"
          maxLength={1000}
          data-testid="chat-input"
        />
        <Button
          type="submit"
          disabled={sending || !content.trim()}
          className="rounded-full bg-plum-700 hover:bg-plum-800 text-white h-11 px-4"
          data-testid="chat-send"
        >
          <Send size={14} />
        </Button>
      </form>
    </div>
  );
}

function RateTab({ tripId, onDone }) {
  const [score, setScore] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!score) return toast.error("Sélectionnez une note");
    setSubmitting(true);
    try {
      await api.post(`/carpool/${tripId}/rating`, { score, comment });
      toast.success("Merci pour votre avis !");
      setDone(true);
      onDone && onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="phenix-card bg-white p-6 text-center" data-testid="rate-done">
        <div className="w-14 h-14 rounded-full bg-mint-100 text-mint-600 grid place-items-center mx-auto mb-2">
          <CheckCircle2 size={28} />
        </div>
        <h3 className="font-display font-extrabold text-plum-900 text-lg">Avis publié — merci !</h3>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="phenix-card bg-white p-5 space-y-3" data-testid="rate-form">
      <div>
        <h3 className="font-display font-extrabold text-plum-900 text-base mb-1">Notez votre trajet</h3>
        <p className="text-[12px] text-plum-700/65">Comment s&apos;est passé le trajet avec ce chauffeur ?</p>
      </div>
      <div className="flex gap-1 justify-center">
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setScore(s)}
            onMouseEnter={() => setHover(s)}
            onMouseLeave={() => setHover(0)}
            className="p-1 transition-transform hover:scale-110"
            data-testid={`star-${s}`}
            aria-label={`Note ${s} sur 5`}
          >
            <Star
              size={32}
              strokeWidth={2}
              className={(hover || score) >= s ? "text-sun-500 fill-sun-500" : "text-plum-700/25"}
            />
          </button>
        ))}
      </div>
      <Textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={3}
        maxLength={280}
        placeholder="Commentaire (optionnel) : ponctualité, ambiance, conduite…"
        className="rounded-xl bg-cream-50"
        data-testid="rate-comment"
      />
      <Button
        type="submit"
        disabled={submitting || !score}
        className="w-full rounded-full bg-plum-700 hover:bg-plum-800 text-white h-11 font-bold"
        data-testid="rate-submit"
      >
        {submitting ? "Envoi…" : "Publier mon avis"}
      </Button>
    </form>
  );
}
